// Angular imports
import { CdkContextMenuTrigger, CdkMenuTrigger } from '@angular/cdk/menu';
import {
  AfterViewInit,
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { EChartOption, SiChartComponent, themeElement, themeSupport } from '@siemens/charts-ng';
import { SiFormModule } from '@siemens/element-ng/form';
import { SiMenuModule } from '@siemens/element-ng/menu';
import { SiResizeObserverDirective } from '@siemens/element-ng/resize-observer';
import { Subscription } from 'rxjs';
// App imports
import { Track, TrackDataSet } from 'src/app/main/shared/DataTypes';
import { SettingsService } from 'src/app/main/shared/services/settings.service';

import { TrackParameterName, TrackService } from '../../track.service';
import { CHART_CONSTANTS, TrackChartBuilderService } from './track-chart-builder.service';
import {
  CommentDialogState,
  TrackPlotCommentComponent
} from './track-plot-comment/track-plot-comment.component';

// Types

interface ContextMenuState {
  show: boolean;
  type: 'add' | null;
  position?: number;
  value?: number;
  dataIndex?: number;
  displayPosition?: string;
  displayValue?: string;
}

// Constants
const POINT_SIZE_EDIT = 7; // Size when edit mode is ON

// Initialize theme
themeSupport.setDefault(themeElement);

@Component({
  selector: 'app-track-plot',
  imports: [
    SiChartComponent,
    SiResizeObserverDirective,
    SiMenuModule,
    CdkMenuTrigger,
    CdkContextMenuTrigger,
    SiFormModule,
    ReactiveFormsModule,
    TrackPlotCommentComponent
  ],
  templateUrl: './track-plot.component.html',
  styleUrl: './track-plot.component.scss'
})
export class TrackPlotComponent implements OnInit, AfterViewInit, OnDestroy {
  // Services

  private readonly trackService = inject(TrackService);
  private readonly settingsService = inject(SettingsService);
  private readonly chartBuilderService = inject(TrackChartBuilderService);

  // Inputs

  readonly track = input.required<Track>();
  readonly dataKey = input.required<TrackParameterName>();

  // Outputs

  readonly dataPointChanged = output<{
    dataKey: TrackParameterName;
    index: number;
    newValue: number;
  }>();

  readonly dataPointAdded = output<{
    dataKey: TrackParameterName;
    position: number;
    value: number;
  }>();

  readonly dataPointDeleted = output<{
    dataKey: TrackParameterName;
    index: number;
  }>();

  readonly commentAdded = output<{
    dataKey: TrackParameterName;
    index: number;
    comment: { date: string; content: string; author: string };
  }>();

  // Public Properties

  readonly chartOption = signal<EChartOption | undefined>(undefined);
  readonly editModeControl = new FormControl(false);
  readonly chartComp = viewChild<SiChartComponent>('chart');
  readonly contextMenuState = signal<ContextMenuState>({ show: false, type: null });

  readonly commentDialogState = signal<CommentDialogState>({
    show: false,
    dataIndex: null,
    position: null,
    oldValue: null,
    newValue: null,
    pixelX: 0,
    pixelY: 0
  });

  // Private Properties

  private dragActiveIndex: number | null = null;
  private dragActiveBaseSeriesIndex: number | null = null;
  private dragActiveOriginalValue: number | null = null;
  private dragListenersAttached = false;
  private subs = new Subscription();
  private selectedPointIndex: number | null = null;
  private keyboardListenerAttached = false;
  private isRebuildingChart = false;

  // Lifecycle Hooks

  ngOnInit(): void {
    this.rebuildChart();
  }

  ngAfterViewInit(): void {
    this.setupEditModeDragIntegration();
    this.setupKeyboardListener();
  }

  ngOnDestroy(): void {
    this.detachDragListeners();
    this.detachKeyboardListener();
    this.subs.unsubscribe();
  }

  // Public Methods

  /**
   * Allows parent to request a chart rebuild when data arrays are immutably replaced
   */
  public rebuildChart(): void {
    // Prevent concurrent rebuilds
    if (this.isRebuildingChart) {
      console.debug('rebuildChart: already in progress, skipping');
      return;
    }

    this.isRebuildingChart = true;

    try {
      this.buildChartOptions();

      // Reconfigure draggable/overlay points in case edit mode is active
      try {
        this.configureDraggablePoints();
      } catch (err) {
        console.debug('rebuildChart: configureDraggablePoints failed', err);
      }

      // Force ECharts to update by calling setOption and then resize
      try {
        const comp: any = this.chartComp?.();
        const instance = this.getEchartsInstance();
        if (instance && this.chartOption()) {
          instance.setOption(this.chartOption(), { notMerge: true, lazyUpdate: false });
        }
        comp?.resize?.();
      } catch (err) {
        console.debug('rebuildChart: chart update failed', err);
      }
    } finally {
      this.isRebuildingChart = false;
    }
  }

  // Event Handlers - Context Menu

  onRightClick(event: MouseEvent): void {
    // Only provide Add in edit mode
    if (!this.editModeControl.value) {
      this.contextMenuState.set({ show: false, type: null });
      return;
    }

    const instance = this.getEchartsInstance();
    if (!instance) return;

    const chartContainer = event.currentTarget as HTMLElement;
    const canvasElement =
      chartContainer.querySelector('canvas') ?? chartContainer.querySelector('svg');
    if (!canvasElement) {
      this.contextMenuState.set({ show: false, type: null });
      return;
    }

    const rect = canvasElement.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    const pointInGrid = instance.convertFromPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [
      offsetX,
      offsetY
    ]);

    if (!pointInGrid || !isFinite(pointInGrid[0]) || !isFinite(pointInGrid[1])) {
      this.contextMenuState.set({ show: false, type: null });
      return;
    }

    let [position, value] = pointInGrid;

    const option = this.chartOption();
    if (!option?.series) {
      this.contextMenuState.set({ show: false, type: null });
      return;
    }

    // Round the position and value
    position = Math.max(0, Math.round(position));
    value = Math.max(0, value);

    const { displayPosition, displayValue, formattedValue } = this.formatContextMenuValues(
      position,
      value
    );

    // Always show add at clicked position when in edit mode
    this.contextMenuState.set({
      show: true,
      type: 'add',
      position: position,
      value: formattedValue,
      displayPosition: displayPosition,
      displayValue: displayValue
    });
  }

  addDataPointAtPosition(): void {
    const state = this.contextMenuState();
    if (state.type !== 'add' || state.position === undefined || state.value === undefined) return;

    this.dataPointAdded.emit({
      dataKey: this.dataKey(),
      position: state.position,
      value: state.value
    });
    this.contextMenuState.set({ show: false, type: null });
  }

  resetContextMenu(): void {
    this.contextMenuState.set({ show: false, type: null });
  }
  downloadChartAsSVG(): void {
    const siChartElement = document.querySelector('si-chart');
    const svgElement = siChartElement!.querySelector('svg');

    if (svgElement) {
      const clonedSvg = svgElement.cloneNode(true) as SVGElement;

      // Force all text elements to black for better visibility
      const textElements = clonedSvg.querySelectorAll('text, tspan');
      textElements.forEach(textEl => {
        (textEl as SVGTextElement).setAttribute('fill', '#000000');
        (textEl as SVGTextElement).style.fill = '#000000';
      });

      // Handle axis lines and grid lines for better contrast
      const pathElements = clonedSvg.querySelectorAll('path, line');
      pathElements.forEach(pathEl => {
        const currentStroke = (pathEl as SVGElement).getAttribute('stroke');
        if (currentStroke && (currentStroke.includes('#fff') || currentStroke.includes('white'))) {
          (pathEl as SVGElement).setAttribute('stroke', '#333333');
        }
      });

      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(clonedSvg);
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `track_plot_${this.dataKey()}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  // Private Methods - Chart Building

  private buildChartOptions(): void {
    const option = this.chartBuilderService.buildChartOptions(this.track(), this.dataKey());
    if (!option) return;

    this.chartOption.set(option);

    // Unsubscribe existing subscriptions to prevent duplicates on rebuild
    this.subs.unsubscribe();
    this.subs = new Subscription();

    // Subscribe to edit mode changes to hide tooltip content during edit mode
    this.subs.add(
      this.editModeControl.valueChanges.subscribe((editMode: boolean | null) => {
        const enabled = !!editMode;
        const current = this.chartOption?.();
        if (!current) return;

        // Hide tooltip content during edit mode but keep axisPointer active
        const updatedTooltip = { ...(current['tooltip'] ?? ({} as any)), showContent: !enabled };
        this.chartOption.set({
          ...current,
          tooltip: updatedTooltip
        } as EChartOption);

        // Clear selection when edit mode is toggled off
        if (!enabled) {
          this.selectedPointIndex = null;
        }
      })
    );

    // Apply initial editMode state if already toggled on
    if (this.editModeControl.value) {
      const cur = this.chartOption?.();
      if (cur) {
        const updatedTooltip = { ...(cur['tooltip'] ?? ({} as any)), showContent: false };
        this.chartOption.set({
          ...cur,
          tooltip: updatedTooltip
        } as EChartOption);
      }
    }
  }

  private getEchartsInstance(): any {
    const comp: any = this.chartComp();
    if (!comp) return null;
    return comp.echartsInstance ?? comp._chart ?? comp.chart ?? null;
  }

  // Private Methods - Edit Mode & Drag/Drop

  /**
   * Checks if a value has actually changed by normalizing both values
   * using the same snapping, rounding, and capping rules applied during editing.
   * This prevents false positives from floating-point precision or snapping.
   * @param oldValue Original value before drag
   * @param newValue New value after drag
   * @returns true if values are meaningfully different
   */
  private hasValueChanged(oldValue: number | null, newValue: number): boolean {
    // If no original value captured, assume change occurred
    if (oldValue === null || !isFinite(oldValue)) {
      return isFinite(newValue);
    }

    if (!isFinite(newValue)) {
      return true;
    }

    // Normalize both values using the same rules applied during editing
    const normalizedOld = this.normalizeValueForComparison(oldValue);
    const normalizedNew = this.normalizeValueForComparison(newValue);

    // For parameters with decimal precision, use tolerance-based comparison
    if (this.dataKey() === 'tunnel_factor_data') {
      // Tunnel factor uses 1 decimal place, so tolerance of 0.05
      return Math.abs(normalizedOld - normalizedNew) > 0.05;
    }

    // For curve data with small curvature values, use tolerance
    if (this.dataKey() === 'curve_data') {
      // Small difference in curvature can be significant, use tolerance
      return Math.abs(normalizedOld - normalizedNew) > 0.001;
    }

    // For other parameters, exact equality after normalization
    return normalizedOld !== normalizedNew;
  }

  /**
   * Normalizes a value for comparison by applying the same snapping,
   * rounding, and capping rules used during editing.
   * @param value The value to normalize
   * @returns Normalized value
   */
  private normalizeValueForComparison(value: number): number {
    if (!isFinite(value)) return value;

    const snapStep = this.getSnapStep(this.dataKey());
    let normalized = value;

    // Apply parameter-specific normalization
    switch (this.dataKey()) {
      case 'curve_data':
        // For curve data, values are stored as curvature but may need radius comparison
        // Keep as-is since we're comparing curvature values directly
        if (snapStep > 0) {
          normalized = Math.round(normalized / snapStep) * snapStep;
        }
        break;

      case 'maximum_speed_data':
        // Apply snapping and cap at 500
        normalized = snapStep > 0 ? Math.round(normalized / snapStep) * snapStep : normalized;
        normalized = Math.min(normalized, 500);
        break;

      case 'tunnel_factor_data':
        // Use 1 decimal place precision
        normalized = snapStep > 0 ? Math.round(normalized / snapStep) * snapStep : normalized;
        normalized = parseFloat(normalized.toFixed(1));
        break;

      case 'elevation_data':
      case 'network_power_data':
      case 'charging_station_data':
      case 'hydrogen_refuel_station_data':
        // Apply snapping and round to integer
        normalized = snapStep > 0 ? Math.round(normalized / snapStep) * snapStep : normalized;
        normalized = Math.round(normalized);
        break;
      case 'curve_radius_data_graz':
        // Apply fine snapping and preserve decimal precision for small values
        normalized = snapStep > 0 ? Math.round(normalized / snapStep) * snapStep : normalized;
        normalized = parseFloat(normalized.toFixed(6));
        break;

      default:
        // Generic: apply snapping and round
        normalized = snapStep > 0 ? Math.round(normalized / snapStep) * snapStep : normalized;
        normalized = Math.round(normalized);
    }

    return normalized;
  }

  private setupEditModeDragIntegration(): void {
    this.subs.add(
      this.editModeControl.valueChanges.subscribe(() => {
        this.configureDraggablePoints();
        if (!this.editModeControl.value) {
          this.selectedPointIndex = null;
        }
      })
    );
  }

  private configureDraggablePoints(): void {
    const instance = this.getEchartsInstance();
    const option = this.chartOption();
    if (!instance || !option) return;

    const editablePrimaryKeys: TrackParameterName[] = [
      'maximum_speed_data',
      'elevation_data',
      'tunnel_factor_data',
      'charging_station_data',
      'hydrogen_refuel_station_data',
      'network_power_data',
      'curve_data',
      'curve_radius_data_graz'
    ];

    const isEditable = !!this.editModeControl.value && editablePrimaryKeys.includes(this.dataKey());
    const targetBaseIndex = this.dataKey() === 'curve_data' ? 1 : 0;
    const baseSeries = (option.series as any[])[targetBaseIndex];
    if (!baseSeries) return;

    const trackDataSets: TrackDataSet[] = this.track().parameters[this.dataKey()];
    const warningFlags = trackDataSets.map(data => data.warning || false);

    baseSeries.symbol = 'circle';

    // Color functions
    const getPointColor = this.createPointColorFunction(warningFlags, isEditable);
    const getHoverColor = this.createHoverColorFunction(warningFlags, isEditable);

    if (isEditable) {
      this.applyEditModeStyles(baseSeries, getPointColor, getHoverColor);
      this.chartOption.set({ ...option });
      this.attachDragListeners();
    } else {
      this.applyViewModeStyles(baseSeries, getPointColor, getHoverColor);
      this.chartOption.set({ ...option });
      this.detachDragListeners();
    }
  }

  private createPointColorFunction(
    warningFlags: boolean[],
    isEditable: boolean
  ): (params: any) => string {
    return (params: any) => {
      const dataIndex = params?.dataIndex;

      if (isEditable && dataIndex === this.selectedPointIndex) {
        return this.settingsService.theme() === 'dark' ? '#333333' : '#000000';
      }

      if (dataIndex !== undefined && warningFlags[dataIndex]) {
        return this.settingsService.theme() === 'dark'
          ? 'hsla(7, 93%, 48%, 0.90)'
          : 'rgba(220, 50, 40, 0.85)';
      }

      return this.settingsService.theme() === 'dark'
        ? 'rgba(80, 200, 120, 0.8)'
        : 'rgba(60, 180, 100, 0.75)';
    };
  }

  private createHoverColorFunction(
    warningFlags: boolean[],
    isEditable: boolean
  ): (params: any) => string {
    return (params: any) => {
      const dataIndex = params?.dataIndex;

      if (isEditable && dataIndex === this.selectedPointIndex) {
        return this.settingsService.theme() === 'dark' ? '#555555' : '#333333';
      }

      if (dataIndex !== undefined && warningFlags[dataIndex]) {
        return this.settingsService.theme() === 'dark'
          ? 'rgba(255, 130, 110, 1)'
          : 'rgba(255, 80, 60, 0.95)';
      }

      return this.settingsService.theme() === 'dark'
        ? 'rgba(100, 230, 150, 1)'
        : 'rgba(80, 210, 130, 0.9)';
    };
  }

  private applyEditModeStyles(
    baseSeries: any,
    getPointColor: (params: any) => string,
    getHoverColor: (params: any) => string
  ): void {
    baseSeries.symbolSize = POINT_SIZE_EDIT;
    baseSeries.itemStyle = {
      color: getPointColor,
      borderWidth: 2,
      borderColor: getPointColor
    };
    baseSeries.emphasis = {
      scale: true,
      scaleSize: 1.3,
      itemStyle: {
        color: getHoverColor,
        borderWidth: 2,
        borderColor: getHoverColor
      }
    };
    baseSeries.z = 900;
  }

  private applyViewModeStyles(
    baseSeries: any,
    getPointColor: (params: any) => string,
    getHoverColor: (params: any) => string
  ): void {
    const trackDataSets: TrackDataSet[] = this.track().parameters[this.dataKey()];
    const warningFlags = trackDataSets.map(data => data.warning || false);

    // Show warning points in view mode, hide normal points
    baseSeries.symbolSize = (val: any, params: any) => {
      return params?.dataIndex !== undefined && warningFlags[params.dataIndex]
        ? CHART_CONSTANTS.POINT.SYMBOL_SIZE_WARNING
        : 0;
    };
    baseSeries.itemStyle = {
      color: getPointColor,
      borderWidth: 0,
      borderColor: getPointColor
    };
    baseSeries.emphasis = {
      scale: false,
      itemStyle: {
        color: getHoverColor,
        borderWidth: 0,
        borderColor: getHoverColor
      }
    };
    delete baseSeries.z;
  }

  private attachDragListeners(): void {
    if (this.dragListenersAttached) return;
    const instance = this.getEchartsInstance();
    if (!instance) return;

    const targetSeriesIndex = this.dataKey() === 'curve_data' ? 1 : 0;

    const onClick = this.createClickHandler(targetSeriesIndex);
    const onMouseDown = this.createMouseDownHandler(targetSeriesIndex);
    const onMouseUp = this.createMouseUpHandler();
    const onMouseMove = this.createMouseMoveHandler();

    (this as any)._dragHandlers = { onClick, onMouseDown, onMouseUp, onMouseMove };
    instance.on('click', onClick);
    instance.on('mousedown', onMouseDown);
    instance.getZr().on('mouseup', onMouseUp);
    instance.getZr().on('mousemove', onMouseMove);
    this.dragListenersAttached = true;
  }

  private createClickHandler(targetSeriesIndex: number): (params: any) => void {
    return (params: any) => {
      if (params.seriesIndex === targetSeriesIndex) {
        const clickedIndex = params.dataIndex;
        this.selectedPointIndex = this.selectedPointIndex === clickedIndex ? null : clickedIndex;
        this.configureDraggablePoints();
      }
    };
  }

  private createMouseDownHandler(targetSeriesIndex: number): (params: any) => void {
    return (params: any) => {
      if (params.seriesIndex === targetSeriesIndex) {
        this.dragActiveIndex = params.dataIndex;
        this.dragActiveBaseSeriesIndex = targetSeriesIndex;

        // Capture the original value before dragging starts
        const option = this.chartOption();
        if (option) {
          const seriesArr = option.series as any[];
          const baseSeries = seriesArr[targetSeriesIndex];
          if (baseSeries?.data?.[params.dataIndex]) {
            this.dragActiveOriginalValue = baseSeries.data[params.dataIndex][1];
          }
        }

        this.configureDraggablePoints();
      }
    };
  }

  private createMouseUpHandler(): () => void {
    return () => {
      if (this.dragActiveIndex !== null && this.dragActiveBaseSeriesIndex !== null) {
        const option = this.chartOption();
        if (option) {
          const seriesArr = option.series as any[];
          const baseSeries = seriesArr[this.dragActiveBaseSeriesIndex];
          if (baseSeries?.data?.[this.dragActiveIndex]) {
            const newVal = baseSeries.data[this.dragActiveIndex][1];
            const positionVal = baseSeries.data[this.dragActiveIndex][0];
            if (
              this.dragActiveBaseSeriesIndex === 0 ||
              (this.dataKey() === 'curve_data' && this.dragActiveBaseSeriesIndex === 1)
            ) {
              // Check if value actually changed before showing dialog
              const originalValue = this.dragActiveOriginalValue;
              const hasChanged = this.hasValueChanged(originalValue, newVal);

              if (hasChanged) {
                // Emit the data change
                this.dataPointChanged.emit({
                  dataKey: this.dataKey(),
                  index: this.dragActiveIndex,
                  newValue: newVal
                });

                // Get pixel coordinates for positioning the comment dialog
                const instance = this.getEchartsInstance();
                if (instance) {
                  try {
                    const useRadiusAxis =
                      this.dataKey() === 'curve_data' && this.dragActiveBaseSeriesIndex === 1;
                    const pixel = instance.convertToPixel(
                      { xAxisIndex: 0, yAxisIndex: useRadiusAxis ? 1 : 0 },
                      [positionVal, newVal]
                    );

                    if (pixel && Array.isArray(pixel) && pixel.length === 2) {
                      // Get chart container position to convert to screen coordinates
                      const canvasElement = instance.getDom();
                      if (canvasElement) {
                        const rect = canvasElement.getBoundingClientRect();
                        const screenX = rect.left + pixel[0];
                        const screenY = rect.top + pixel[1];

                        // Show comment dialog near the edited point
                        this.commentDialogState.set({
                          show: true,
                          dataIndex: this.dragActiveIndex,
                          position: positionVal,
                          oldValue: originalValue,
                          newValue: newVal,
                          pixelX: screenX,
                          pixelY: screenY
                        });
                      }
                    }
                  } catch (err) {
                    console.debug('Failed to get pixel coordinates for comment dialog', err);
                  }
                }
              }
            }
          }
        }
      }
      this.dragActiveIndex = null;
      this.dragActiveBaseSeriesIndex = null;
      this.dragActiveOriginalValue = null;
      this.configureDraggablePoints();
    };
  }

  private createMouseMoveHandler(): (e: any) => void {
    return (e: any) => {
      if (this.dragActiveIndex === null || this.dragActiveBaseSeriesIndex === null) return;
      const ec = this.getEchartsInstance();
      if (!ec) return;

      const useRadiusAxis = this.dataKey() === 'curve_data' && this.dragActiveBaseSeriesIndex === 1;
      const pointInGrid = ec.convertFromPixel(
        { xAxisIndex: 0, yAxisIndex: useRadiusAxis ? 1 : 0 },
        [e.event.offsetX, e.event.offsetY]
      );
      if (!pointInGrid) return;

      const yVal = pointInGrid[1];
      const option = this.chartOption();
      if (!option) return;

      if (this.dataKey() === 'curve_data' && useRadiusAxis) {
        this.handleCurveDrag(yVal, option);
      } else {
        this.handleStandardDrag(yVal, option);
      }
    };
  }

  private handleCurveDrag(yVal: number, option: EChartOption): void {
    let radius = isFinite(yVal) ? yVal : CHART_CONSTANTS.CURVE.MIN_RADIUS;
    const snapStep = this.getSnapStep(this.dataKey());
    radius = snapStep > 0 ? Math.round(radius / snapStep) * snapStep : radius;
    radius = Math.min(
      CHART_CONSTANTS.CURVE.MAX_RADIUS,
      Math.max(CHART_CONSTANTS.CURVE.MIN_RADIUS, radius)
    );

    const seriesArr = [...(option.series as any[])];
    const radiusSeries = seriesArr[this.dragActiveBaseSeriesIndex!];
    const curvatureSeries = seriesArr[0];
    if (!radiusSeries || !curvatureSeries) return;

    const originalX = radiusSeries.data[this.dragActiveIndex!][0];
    radiusSeries.data[this.dragActiveIndex!] = [originalX, radius];
    const curvature = radius > 0 ? 1 / radius : CHART_CONSTANTS.CURVE.MIN_CURVATURE;
    curvatureSeries.data[this.dragActiveIndex!] = [
      originalX,
      Math.max(CHART_CONSTANTS.CURVE.MIN_CURVATURE, curvature)
    ];
    this.persistDraggedValue(this.dragActiveIndex!, radius);
    this.chartOption.set({ ...option, series: seriesArr });
  }

  private handleStandardDrag(yVal: number, option: EChartOption): void {
    let axisMin = 0;
    const yAxis = option['yAxis'] as any;
    const primaryYAxis = Array.isArray(yAxis) ? yAxis[0] : yAxis;
    if (primaryYAxis && typeof primaryYAxis.min === 'number') axisMin = primaryYAxis.min;
    const unclampedY = Math.max(axisMin, yVal);

    const snapStep = this.getSnapStep(this.dataKey());
    let snappedY = snapStep > 0 ? Math.round(unclampedY / snapStep) * snapStep : unclampedY;

    if (this.dataKey() === 'maximum_speed_data') {
      snappedY = Math.min(snappedY, 500);
    }

    const seriesArr = [...(option.series as any[])];
    const baseSeries = seriesArr[this.dragActiveBaseSeriesIndex!];
    if (!baseSeries) return;

    const originalX = baseSeries.data[this.dragActiveIndex!][0];
    baseSeries.data[this.dragActiveIndex!] = [originalX, snappedY];

    if (this.dragActiveBaseSeriesIndex === 0) {
      this.persistDraggedValue(this.dragActiveIndex!, snappedY);
    }

    // Update y-axis max for maximum_speed_data during drag
    if (this.dataKey() === 'maximum_speed_data') {
      const currentMaxValue = Math.max(...baseSeries.data.map((point: any) => point[1]));
      const updatedYAxis = { ...primaryYAxis, max: currentMaxValue + 20 };
      const updatedOption = {
        ...option,
        series: seriesArr,
        yAxis: Array.isArray(option['yAxis'])
          ? [updatedYAxis, ...option['yAxis'].slice(1)]
          : updatedYAxis
      };
      this.chartOption.set(updatedOption);
    } else {
      this.chartOption.set({ ...option, series: seriesArr });
    }
  }

  private detachDragListeners(): void {
    if (!this.dragListenersAttached) return;
    const instance = this.getEchartsInstance();
    const handlers = (this as any)._dragHandlers;
    if (instance && handlers) {
      instance.off('click', handlers.onClick);
      instance.off('mousedown', handlers.onMouseDown);
      const zr = instance.getZr();
      if (zr) {
        zr.off('mouseup', handlers.onMouseUp);
        zr.off('mousemove', handlers.onMouseMove);
      }
    }
    this.dragListenersAttached = false;
    this.dragActiveIndex = null;
    this.dragActiveBaseSeriesIndex = null;
  }

  private persistDraggedValue(index: number, newValue: number): void {
    try {
      const trackVal = this.track();
      if (!trackVal?.parameters) {
        console.warn('persistDraggedValue: track or parameters undefined');
        return;
      }

      const dataArray: TrackDataSet[] = trackVal.parameters[this.dataKey()];
      if (!Array.isArray(dataArray)) {
        console.warn('persistDraggedValue: dataArray is not an array');
        return;
      }

      if (index < 0 || index >= dataArray.length) {
        console.warn(
          `persistDraggedValue: index ${index} out of bounds (length: ${dataArray.length})`
        );
        return;
      }

      const target = dataArray[index];
      if (!target) {
        console.warn(`persistDraggedValue: target at index ${index} is undefined`);
        return;
      }

      const valueToSet =
        this.dataKey() === 'maximum_speed_data' ? Math.min(newValue, 500) : newValue;
      target.trackParameter1 = valueToSet;
    } catch (err) {
      console.error('persistDraggedValue: failed', err);
    }
  }

  private getSnapStep(key: TrackParameterName): number {
    return this.chartBuilderService.getSnapStep(key);
  }

  // Private Methods - Keyboard Handlers

  private setupKeyboardListener(): void {
    if (this.keyboardListenerAttached) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        this.editModeControl.value &&
        this.selectedPointIndex !== null &&
        (event.key === 'Delete' || event.key === 'Backspace')
      ) {
        event.preventDefault();
        this.deleteSelectedPoint();
      }
    };

    (this as any)._keyboardHandler = onKeyDown;
    document.addEventListener('keydown', onKeyDown);
    this.keyboardListenerAttached = true;
  }

  private detachKeyboardListener(): void {
    if (!this.keyboardListenerAttached) return;
    const handler = (this as any)._keyboardHandler;
    if (handler) {
      document.removeEventListener('keydown', handler);
    }
    this.keyboardListenerAttached = false;
  }

  private deleteSelectedPoint(): void {
    if (this.selectedPointIndex === null) return;

    this.dataPointDeleted.emit({
      dataKey: this.dataKey(),
      index: this.selectedPointIndex
    });

    this.selectedPointIndex = null;

    setTimeout(() => {
      this.rebuildChart();
    }, 0);
  }

  // Private Methods - Utilities

  private formatContextMenuValues(
    position: number,
    value: number
  ): { displayPosition: string; displayValue: string; formattedValue: number } {
    const parameterInfo = this.trackService.parameterNameMappings[this.dataKey()];
    const displayPosition = `${position}m`;
    let displayValue: string;
    let formattedValue: number;

    if (this.dataKey() === 'curve_data') {
      formattedValue = this.formatCurveValue(value);
      displayValue = `${formattedValue}m`;
    } else if (this.dataKey() === 'elevation_data') {
      formattedValue = this.applySnapping(value);
      const unit = parameterInfo?.['trackParameter1']?.unit ?? 'm';
      displayValue = `${formattedValue}${unit}`;
    } else if (this.dataKey() === 'maximum_speed_data') {
      formattedValue = Math.min(this.applySnapping(value), 500);
      const unit = parameterInfo?.['trackParameter1']?.unit ?? 'km/h';
      displayValue = `${formattedValue}${unit}`;
    } else if (this.dataKey() === 'tunnel_factor_data') {
      formattedValue = parseFloat(this.applySnapping(value).toFixed(1));
      displayValue = `${formattedValue}`;
    } else if (this.dataKey() === 'network_power_data') {
      formattedValue = this.applySnapping(value);
      const unit = parameterInfo?.['trackParameter1']?.unit ?? 'kW';
      displayValue = `${formattedValue}${unit}`;
    } else if (this.dataKey() === 'curve_radius_data_graz') {
      formattedValue = parseFloat(this.applySnapping(value).toFixed(6));
      const unit = parameterInfo?.['trackParameter1']?.unit ?? '1/m';
      displayValue = `${formattedValue}${unit}`;
    } else if (
      this.dataKey() === 'charging_station_data' ||
      this.dataKey() === 'hydrogen_refuel_station_data'
    ) {
      formattedValue = Math.round(value);
      const unit = parameterInfo?.['trackParameter1']?.unit ?? '';
      displayValue = `${formattedValue}${unit}`;
    } else {
      formattedValue = Math.round(value);
      const unit = parameterInfo?.['trackParameter1']?.unit ?? '';
      displayValue = `${formattedValue}${unit}`;
    }

    return { displayPosition, displayValue, formattedValue };
  }

  private formatCurveValue(value: number): number {
    let radius: number;
    if (value === 0 || value < CHART_CONSTANTS.CURVE.MIN_CURVATURE) {
      radius = 10000;
    } else {
      radius = Math.round(1 / value);
    }
    radius = Math.min(
      CHART_CONSTANTS.CURVE.MAX_RADIUS,
      Math.max(CHART_CONSTANTS.CURVE.MIN_RADIUS, radius)
    );

    const snapStep = this.getSnapStep(this.dataKey());
    if (snapStep > 0) {
      radius = Math.round(radius / snapStep) * snapStep;
    }

    return radius;
  }

  private applySnapping(value: number): number {
    const snapStep = this.getSnapStep(this.dataKey());
    return snapStep > 0 ? Math.round(value / snapStep) * snapStep : Math.round(value);
  }

  // Comment Dialog Event Handlers

  /**
   * Handles comment saved event from the comment component
   */
  onCommentSaved(event: {
    dataKey: TrackParameterName;
    index: number;
    comment: { date: string; content: string; author: string };
  }): void {
    this.commentAdded.emit(event);
  }

  /**
   * Handles comment dialog close event from the comment component
   */
  onCommentClosed(): void {
    this.commentDialogState.set({
      show: false,
      dataIndex: null,
      position: null,
      oldValue: null,
      newValue: null,
      pixelX: 0,
      pixelY: 0
    });
    this.dragActiveOriginalValue = null;
  }
}
